import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AddIcon, ChevronRightIcon, MoreHorizIcon, StarIcon } from "../../shared/components/icons";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import { CatalogManageSheet } from "./CatalogManageSheet";
import { CreateCatalogDialog } from "./CreateCatalogDialog";
import { LevelChip } from "./LevelChip";
import { useDemoMode } from "../demo/useDemoMode";
import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";

type CurrentCatalog =
  | { kind: "own"; entry: OwnCatalogEntry }
  | { kind: "shared"; entry: SharedCatalogEntry };

/**
 * Catalog home (catalog-selector 02, verdict 5b): the current catalog on top
 * (name, owner/level, default star, Browse, manage), then “My catalogs” and
 * “Shared with me” for the other catalogs, each row switching on tap and
 * returning to Browse. The page stays the management home even with one
 * catalog; create stays here with the new catalog current.
 *
 * The content header renders the page `h1` (“Catalogs”), so this view starts at
 * `h2` for its sections.
 */
export function CatalogsView() {
  const t = useT();
  const demoMode = useDemoMode();
  const navigate = useNavigate();
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const selectEntry = useAppStore((s) => s.selectEntry);

  const [manageCatalog, setManageCatalog] = useState<OwnCatalogEntry | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const current = resolveCurrent(entries, sharedWithMe, selectedEntryId);
  const currentId = current ? catalogId(current) : undefined;
  const otherOwn = alphabetical(entries.filter((entry) => entry.id !== currentId), (entry) => entry.name);
  const otherShared = alphabetical(
    sharedWithMe.filter((candidate) => candidate.entry.id !== currentId),
    (candidate) => candidate.entry.name,
  );

  /** A row switches the entry and returns to Browse (verdict 3a). */
  const switchTo = async (id: string) => {
    try {
      await selectEntry(id);
    } catch (err) {
      log.warn({ err, entryId: id.slice(0, 7) }, "[catalog] Switch catalog failed");
    } finally {
      navigate("/browse");
    }
  };

  return (
    <Box data-testid="catalogs-view">
      {current ? (
        <CurrentCatalogBlock
          current={current}
          onBrowse={() => navigate("/browse")}
          onManage={
            demoMode ? undefined : () => current.kind === "own" && setManageCatalog(current.entry)
          }
        />
      ) : (
        <Typography variant="body2" color="text.secondary" data-testid="catalogs-empty" sx={{ mb: 2 }}>
          {t("entryEmpty")}
        </Typography>
      )}

      {otherOwn.length > 0 && (
        <Box component="section" data-testid="catalog-own-list" sx={{ mb: 2 }}>
          <Typography component="h2" variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t("myCatalogs")}
          </Typography>
          <List disablePadding>
            {otherOwn.map((entry) => (
              <CatalogRow
                key={entry.id}
                name={entry.name}
                defaultEntry={entry.isDefault}
                onSelect={() => void switchTo(entry.id)}
                onManage={demoMode ? undefined : () => setManageCatalog(entry)}
              />
            ))}
          </List>
        </Box>
      )}

      {otherShared.length > 0 && (
        <Box component="section" data-testid="catalog-shared-list" sx={{ mb: 2 }}>
          <Typography component="h2" variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t("sharedWithMe")}
          </Typography>
          <List disablePadding>
            {otherShared.map((shared) => (
              <CatalogRow
                key={shared.entry.id}
                name={shared.entry.name}
                caption={t("sharedFrom", { owner: shared.owner })}
                level={shared.level}
                onSelect={() => void switchTo(shared.entry.id)}
              />
            ))}
          </List>
        </Box>
      )}

      {!demoMode && (
        <>
          <Button
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setCreateOpen(true)}
            data-testid="catalog-new"
            sx={{ minHeight: 44 }}
          >
            {t("newEntry")}
          </Button>

          <CreateCatalogDialog open={createOpen} onClose={() => setCreateOpen(false)} />
        </>
      )}

      {!demoMode && manageCatalog && (
        <CatalogManageSheet catalog={manageCatalog} onClose={() => setManageCatalog(null)} />
      )}
    </Box>
  );
}

/** The current catalog: identity, default star and the two page actions. */
function CurrentCatalogBlock({
  current,
  onBrowse,
  onManage,
}: {
  current: CurrentCatalog;
  onBrowse: () => void;
  onManage?: () => void;
}) {
  const t = useT();
  const own = current.kind === "own" ? current.entry : undefined;
  const shared = current.kind === "shared" ? current.entry : undefined;
  const name = own?.name ?? shared?.entry.name ?? "";
  const isDefault = own?.isDefault ?? shared?.entry.isDefault ?? false;
  const level = shared?.level ?? "owner";

  return (
    <Paper variant="outlined" data-testid="catalog-current" sx={{ p: 1.5, mb: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: "wrap", rowGap: 1 }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("currentCatalog")}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              component="p"
              noWrap
              data-testid="catalog-current-name"
              sx={{ fontWeight: 650, m: 0 }}
            >
              {name}
            </Typography>
            {isDefault && (
              <Box
                data-testid="catalog-current-default"
                sx={{ display: "flex", color: "warning.main" }}
              >
                <StarIcon fontSize="small" />
              </Box>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
            <LevelChip level={level} />
            {shared && (
              <Typography variant="caption" color="text.secondary">
                {t("sharedFrom", { owner: shared.owner })}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={onBrowse}
            data-testid="catalog-browse"
            sx={{ minHeight: 44 }}
          >
            {t("browse")}
          </Button>
          {own && onManage && (
            <Button
              variant="outlined"
              size="small"
              onClick={onManage}
              data-testid="catalog-manage"
              sx={{ minHeight: 44 }}
            >
              {t("manage")}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * One other catalog: the row switches; a trailing manage action opens the same
 * sheet without switching, so every own catalog is manageable in place
 * (verdict 4b). Shared rows stay read-only.
 */
function CatalogRow({
  name,
  caption,
  level,
  defaultEntry = false,
  onSelect,
  onManage,
}: {
  name: string;
  caption?: string;
  level?: SharedCatalogEntry["level"];
  defaultEntry?: boolean;
  onSelect: () => void;
  onManage?: () => void;
}) {
  const t = useT();
  return (
    <Stack direction="row" alignItems="center">
      <ListItemButton
        onClick={onSelect}
        data-testid={`catalog-row-${name}`}
        sx={{ minHeight: 56, borderRadius: 1, px: 1, flex: 1 }}
      >
      <ListItemText
        primary={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" noWrap>
              {name}
            </Typography>
            {defaultEntry && (
              <Box sx={{ display: "flex", color: "warning.main" }}>
                <StarIcon fontSize="small" />
              </Box>
            )}
          </Stack>
        }
        secondary={caption}
      />
        {level && <LevelChip level={level} />}
        <ChevronRightIcon fontSize="small" />
      </ListItemButton>
      {onManage && (
        <IconButton
          onClick={onManage}
          aria-label={`${t("manage")}: ${name}`}
          data-testid={`catalog-manage-${name}`}
          sx={{ width: 44, height: 44, ml: 0.5 }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

/**
 * The current catalog from the store selection: the selected own or shared
 * catalog, falling back to the own default/first and then the first shared —
 * the same order the store resolves when a catalog disappears.
 */
function resolveCurrent(
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
  selectedEntryId?: string,
): CurrentCatalog | undefined {
  const own = entries.find((entry) => entry.id === selectedEntryId);
  if (own) return { kind: "own", entry: own };
  const shared = sharedWithMe.find((candidate) => candidate.entry.id === selectedEntryId);
  if (shared) return { kind: "shared", entry: shared };
  const fallbackOwn = entries.find((entry) => entry.isDefault) ?? entries[0];
  if (fallbackOwn) return { kind: "own", entry: fallbackOwn };
  const fallbackShared = sharedWithMe[0];
  return fallbackShared ? { kind: "shared", entry: fallbackShared } : undefined;
}

function catalogId(current: CurrentCatalog): string {
  return current.kind === "own" ? current.entry.id : current.entry.entry.id;
}

/** Lists are alphabetical by catalog name (verdict 5b). */
function alphabetical<T>(catalogs: readonly T[], nameOf: (catalog: T) => string): T[] {
  return [...catalogs].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
}
